import { isWithinInterval } from 'date-fns';

/**
 * Check if a date falls within a specified date range (inclusive)
 *
 * Returns true if the date is within the range [start, end], including the boundaries.
 * The start date must be before or equal to the end date.
 *
 * @param date - Date to check (Date object or ISO string)
 * @param start - Start of the range (Date object or ISO string)
 * @param end - End of the range (Date object or ISO string)
 *
 * @returns true if the date is within the range (inclusive)
 *
 * @example
 * ```ts
 * // Date within range
 * isWithinRange('2025-01-15', '2025-01-10', '2025-01-20')
 * // => true
 *
 * // Date equals start boundary
 * isWithinRange('2025-01-10', '2025-01-10', '2025-01-20')
 * // => true
 *
 * // Date equals end boundary
 * isWithinRange('2025-01-20', '2025-01-10', '2025-01-20')
 * // => true
 *
 * // Date before range
 * isWithinRange('2025-01-05', '2025-01-10', '2025-01-20')
 * // => false
 *
 * // Date after range
 * isWithinRange('2025-01-25', '2025-01-10', '2025-01-20')
 * // => false
 * ```
 */
export const isWithinRange = (date: Date | string, start: Date | string, end: Date | string): boolean => {
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const endDate = typeof end === 'string' ? new Date(end) : end;

  return isWithinInterval(targetDate, { end: endDate, start: startDate });
};
